import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { getApiErrorMessage } from '../api/apiClient';
import { useAuth } from '../auth/AuthContext';
import logo from '../assets/company-logo.png';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login, passwordChanged } = useAuth();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const redirectTo = location.state?.from?.pathname || '/workflows';

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await login(form);
      navigate(redirectTo, { replace: true });
    } catch (loginError) {
      setError(getApiErrorMessage(loginError, 'Unable to sign in with those credentials.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="mx-auto flex min-h-screen max-w-6xl items-center justify-center px-6 py-12">
      <section className="grid w-full overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl md:grid-cols-[1fr_420px]">
        <div className="hidden bg-slate-950 p-10 text-white md:flex md:flex-col md:justify-between">
          <img src={logo} alt="Company logo" className="h-14 w-fit rounded bg-white p-2" />
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-slate-400">RNA Analytics</p>
            <h1 className="mt-4 text-4xl font-semibold leading-tight">
              Sign in to continue to the workflow workspace.
            </h1>
            <p className="mt-4 max-w-md text-sm leading-6 text-slate-300">
              Authentication uses the existing Django JWT backend. Access tokens are attached automatically to API requests.
            </p>
          </div>
        </div>

        <div className="p-8 sm:p-10">
          <div className="mb-8 md:hidden">
            <img src={logo} alt="Company logo" className="h-12 w-auto" />
          </div>

          <h2 className="text-2xl font-semibold text-slate-950">Login</h2>
          <p className="mt-2 text-sm text-slate-500">Use your backend username and password.</p>

          {(passwordChanged || location.state?.passwordChanged === true) && (
            <p role="status" className="mt-4 text-sm text-green-700">
              Password changed. Sign in with your new password.
            </p>
          )}

          <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-slate-700">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                value={form.username}
                onChange={handleChange}
                required
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-950 outline-none ring-slate-900 transition focus:border-slate-900 focus:ring-2"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={form.password}
                onChange={handleChange}
                required
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-950 outline-none ring-slate-900 transition focus:border-slate-900 focus:ring-2"
              />
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-lg bg-slate-950 px-4 py-2.5 font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
            >
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}
